import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from "./components/ui/sonner";

// Analytics
import { initGA, usePageTracking } from "./utils/analytics";

// SEO Components
import StructuredData from "./components/StructuredData";

// Components
import Header from "./components/Header";
import Footer from "./components/Footer";
import DisclaimerModal from "./components/DisclaimerModal";
import FloatingButtons from "./components/FloatingButtons";
import CookieConsent from "./components/CookieConsent";
import ScrollToTop from "./components/ScrollToTop";
import AccessibilityBar from "./components/AccessibilityBar";

// Pages
import HomePage from "./pages/HomePage";
import ServicesPage from "./pages/ServicesPage";
import AboutPage from "./pages/AboutPage";
import ContactPage from "./pages/ContactPage";
import InsightsPage from "./pages/InsightsPage";
import PricingPage from "./pages/PricingPage";
import ComplaintBoardPage from "./pages/ComplaintBoardPage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import DisclaimerPage from "./pages/DisclaimerPage";
import RefundPage from "./pages/RefundPage";
import MITCPage from "./pages/MITCPage";
import InvestorCharterPage from "./pages/InvestorCharterPage";
import AmlPolicyPage from "./pages/AmlPolicyPage";
import GrievancePage from "./pages/GrievancePage";
import AccessibilityPage from "./pages/AccessibilityPage";
import LandingPage from "./pages/LandingPage";

// Wrapper for main site pages (with Header, Footer, etc.)
function MainSiteLayout({ children }) {
  return (
    <>
      <AccessibilityBar />
      <ScrollToTop />
      <StructuredData />
      <DisclaimerModal />
      <Header />
      <FloatingButtons />
      <main id="main-content" className="pt-9">{children}</main>
      <Footer />
      <CookieConsent />
    </>
  );
}

// Standalone layout for landing pages (no Header/Footer/Modals)
function StandaloneLayout({ children }) {
  return (
    <>
      <a href="#main-content" className="skip-to-main">
        Skip to main content
      </a>
      <ScrollToTop />
      <main id="main-content">{children}</main>
    </>
  );
}

function AppContent() {
  usePageTracking(); // Track page views
  const location = useLocation();
  
  // Check if current route is a standalone page
  const standaloneRoutes = ['/promo', '/landing'];
  const isStandalone = standaloneRoutes.some(route => location.pathname.startsWith(route));

  if (isStandalone) {
    return (
      <StandaloneLayout>
        <Routes>
          <Route path="/promo" element={<LandingPage />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </StandaloneLayout>
    );
  }

  return (
    <MainSiteLayout>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/insights" element={<InsightsPage />} />
        <Route path="/complaint-board" element={<ComplaintBoardPage />} />
        <Route path="/privacy-policy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/disclaimer" element={<DisclaimerPage />} />
        <Route path="/refund-policy" element={<RefundPage />} />
        <Route path="/mitc" element={<MITCPage />} />
        <Route path="/investor-charter" element={<InvestorCharterPage />} />
        <Route path="/aml-policy" element={<AmlPolicyPage />} />
        <Route path="/grievance" element={<GrievancePage />} />
        <Route path="/accessibility" element={<AccessibilityPage />} />
      </Routes>
      <Toaster position="top-right" richColors />
    </MainSiteLayout>
  );
}

function App() {
  useEffect(() => {
    // Initialize Google Analytics
    initGA();
  }, []);

  return (
    <HelmetProvider>
      <div className="App min-h-screen">
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
